import type { WeekStartDay } from "@/types/domain";

export const DEFAULT_WEEK_START_DAY: WeekStartDay = 1; // Mon

export interface WeekBounds {
  start: Date;
  end: Date;
  iso_week: number;
  year: number;
  start_day_offset: WeekStartDay;
  length_days: number;
}

const MS_PER_DAY = 86_400_000;

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function daysBetween(a: Date, b: Date): number {
  return Math.round(
    (startOfDay(b).getTime() - startOfDay(a).getTime()) / MS_PER_DAY,
  );
}

/** ISO week + ISO-week-numbering year for `date` (anchored on its Thursday). */
function isoLabelForDate(date: Date): { iso_week: number; year: number } {
  const d = startOfDay(date);
  // Move to the Thursday of this ISO week — its calendar year IS the ISO year.
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const w1 = new Date(d.getFullYear(), 0, 4);
  const isoWeek =
    1 +
    Math.round(
      ((d.getTime() - w1.getTime()) / MS_PER_DAY -
        3 +
        ((w1.getDay() + 6) % 7)) /
        7,
    );
  return { iso_week: isoWeek, year: d.getFullYear() };
}

/** Bounds of the 7-day week containing `date` under `startDayOffset`. */
export function weekContaining(
  date: Date,
  startDayOffset: WeekStartDay = DEFAULT_WEEK_START_DAY,
): WeekBounds {
  const today = startOfDay(date);
  const daysFromStart = (today.getDay() - startDayOffset + 7) % 7;
  const start = addDays(today, -daysFromStart);
  const end = addDays(start, 6);
  // Label by the Thursday in the range (Thursday is always present in any
  // 7-day window).
  const thursdayOffset = (4 - startDayOffset + 7) % 7;
  const label = isoLabelForDate(addDays(start, thursdayOffset));
  return {
    start,
    end,
    iso_week: label.iso_week,
    year: label.year,
    start_day_offset: startDayOffset,
    length_days: 7,
  };
}

/**
 * Bounds for the first week under `newOffset` after a setting flip whose
 * previous week ended on `previousWeekEnd`. Starts the day after; ends on
 * the first natural end-of-week ≥ start; minimum 4 days, else extend by 7.
 */
export function transitionWeekAfter(
  previousWeekEnd: Date,
  newOffset: WeekStartDay,
): WeekBounds {
  const start = addDays(startOfDay(previousWeekEnd), 1);
  const endDayOfWeek = (newOffset + 6) % 7; // day-of-week of the last day under newOffset
  const daysToEnd = (endDayOfWeek - start.getDay() + 7) % 7;
  let end = addDays(start, daysToEnd);
  let length = daysBetween(start, end) + 1;
  if (length < 4) {
    end = addDays(end, 7);
    length += 7;
  }
  // Label by the Thursday in the range; for transitions ≥ 4 days a Thursday
  // is guaranteed to fall inside.
  const thursdayOffsetFromStart = (4 - start.getDay() + 7) % 7;
  const label = isoLabelForDate(addDays(start, thursdayOffsetFromStart));
  return {
    start,
    end,
    iso_week: label.iso_week,
    year: label.year,
    start_day_offset: newOffset,
    length_days: length,
  };
}

/**
 * Synthesized bounds for the week labelled (isoWeek, year) under
 * `startDayOffset`: the 7-day window containing that ISO week's Thursday.
 */
export function weekWithLabel(
  isoWeek: number,
  year: number,
  startDayOffset: WeekStartDay = DEFAULT_WEEK_START_DAY,
): WeekBounds {
  const isoMonday = synthesizeIsoMondayBounds(isoWeek, year).start;
  return weekContaining(addDays(isoMonday, 3), startDayOffset);
}

/** Bounds for the week containing today, given the family's offset. */
export function currentWeekBounds(
  startDayOffset: WeekStartDay = DEFAULT_WEEK_START_DAY,
  today: Date = new Date(),
): WeekBounds {
  return weekContaining(today, startDayOffset);
}

const NB_MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "mai",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "des",
];

// Indexed by JS getDay() (0=Sunday).
const NB_DAYS_FULL = [
  "Søndag",
  "Mandag",
  "Tirsdag",
  "Onsdag",
  "Torsdag",
  "Fredag",
  "Lørdag",
];
const NB_DAYS_SHORT = ["søn", "man", "tir", "ons", "tor", "fre", "lør"];

/** E.g. "man 27." — day-tab label in week pickers. */
export function formatDayTabLabel(d: Date): string {
  return `${NB_DAYS_SHORT[d.getDay()]} ${d.getDate()}.`;
}

/** E.g. "Mandag 27. apr" — heading for a day group of logs. */
export function formatDayHeading(d: Date): string {
  return `${NB_DAYS_FULL[d.getDay()]} ${d.getDate()}. ${NB_MONTHS[d.getMonth()]}`;
}

/** E.g. "21–27 apr" or "29 apr–5 mai". */
export function formatWeekRange(start: Date, end: Date): string {
  const s = startOfDay(start);
  const e = startOfDay(end);
  if (s.getMonth() === e.getMonth()) {
    return `${s.getDate()}–${e.getDate()} ${NB_MONTHS[s.getMonth()]}`;
  }
  return `${s.getDate()} ${NB_MONTHS[s.getMonth()]}–${e.getDate()} ${NB_MONTHS[e.getMonth()]}`;
}

/**
 * Caption for the week the child is currently viewing, used in stat labels
 * like "tjent {…}". Returns "denne uka" normally, or "forrige uke (uke N)"
 * during the previous-week lockout so totals aren't mistaken for the current
 * week. Callers render it uppercase via CSS.
 */
export function viewedWeekLabel(
  isPreviousWeek: boolean,
  isoWeek: number,
): string {
  return isPreviousWeek ? `forrige uke (uke ${isoWeek})` : "denne uka";
}

/** Format Date as local YYYY-MM-DD (no timezone shift). */
export function formatDateOnly(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Parse a YYYY-MM-DD string as a local Date at midnight. */
export function parseDateOnly(s: string): Date {
  const [y, m, d] = s.split("-").map((p) => Number.parseInt(p, 10));
  return new Date(y, m - 1, d);
}

/** True if `date` is the last day of the week under `startDayOffset`. */
export function isLastDayOfWeek(
  startDayOffset: WeekStartDay = DEFAULT_WEEK_START_DAY,
  date: Date = new Date(),
): boolean {
  const endDayOfWeek = (startDayOffset + 6) % 7;
  return date.getDay() === endDayOfWeek;
}

/** Build bounds from a stored week record's explicit dates. */
export function boundsFromWeekRecord(record: {
  start_date?: string;
  end_date?: string;
  iso_week: number;
  year: number;
  start_day_offset?: WeekStartDay;
}): WeekBounds {
  if (record.start_date && record.end_date) {
    const start = parseDateOnly(record.start_date);
    const end = parseDateOnly(record.end_date);
    const offset = (record.start_day_offset ??
      DEFAULT_WEEK_START_DAY) as WeekStartDay;
    return {
      start,
      end,
      iso_week: record.iso_week,
      year: record.year,
      start_day_offset: offset,
      length_days: daysBetween(start, end) + 1,
    };
  }
  // Legacy records: synthesize Mon–Sun bounds from iso_week + year.
  return synthesizeIsoMondayBounds(record.iso_week, record.year);
}

function synthesizeIsoMondayBounds(isoWeek: number, year: number): WeekBounds {
  const jan4 = new Date(year, 0, 4);
  const mondayOfWeek1 = addDays(jan4, -((jan4.getDay() + 6) % 7));
  const start = addDays(mondayOfWeek1, (isoWeek - 1) * 7);
  const end = addDays(start, 6);
  return {
    start,
    end,
    iso_week: isoWeek,
    year,
    start_day_offset: 1,
    length_days: 7,
  };
}
